// Behavioral tracking

// React & Router
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  fetchFormConfig,
  fetchFormState,
  fetchPages,
  fetchQuestions,
  submitAnswers,
} from "../../../redux/slices/general-form.slice.js";

// Hooks
import { useFormAnswers } from "../../../hooks/useFormAnswers.js";

// Components
import BehaviorTrackerService from "../../../services/form-services/internal-behavior-tracker.service.js";
import CustomButton from "../../atoms/custom-button.atom.jsx";
import WaveBackground from "../../atoms/wave-background.atom.jsx";
import FormPage from "../../templates/form-page.template.jsx";
import FormScreenTemplate from "../../templates/form-screen.template.jsx";
import StepFormTemplate from "../../templates/step-form.template.jsx";

// Utils
import { getFormConfigState } from "../../../utils/generalFormUtils.js";

// Styles
import { styles } from "./styles/styles.js";

const GenericForm = ({ type_of_form = "credit_line" }) => {
  const dispatch = useDispatch();
  const { web_uri } = useParams();

  const {
    answers,
    status: submitStatus,
    error: submitError,
    validationErrors,
    setAnswerValue,
    validateAnswers,
  } = useFormAnswers();

  // Redux selectors
  const {
    form_found,
    form_already_submitted,
    ya_fue_completado,
    completed,
    form_id,
    loading: formStateLoading,
    error: formStateError,
  } = useSelector((state) => state.formState);
  const { pages, questions } = useSelector((state) => state.form);
  const formConfig = useSelector((state) => state.formConfig.data);
  const formConfigLoading = useSelector((state) => state.formConfig.loading);

  // Local state
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);

  // Memoized derived values
  const sortedPages = useMemo(
    () => [...pages].sort((a, b) => a.page_order - b.page_order),
    [pages]
  );
  const totalSteps = sortedPages.length;
  const currentPage = sortedPages[currentIndex] || {};
  const title = currentPage.title || "Sin título";
  const description = title;

  const config = getFormConfigState(
    formConfig,
    form_found,
    form_already_submitted,
    ya_fue_completado,
    completed,
    currentIndex,
    totalSteps,
    formSubmitted
  );

  useEffect(() => {
    BehaviorTrackerService.startTracking();
  }, []);

  const configBody = config?.body ?? {};
  const { fito, hero, body: bodyText, button } = configBody;
  const configFooter = config?.footer ?? {};
  const { image } = configFooter;

  let footerIcon = null;

  // new styles of footers to be implemented
  if (image === "just-wave") {
    footerIcon = <WaveBackground />;
  } else if (image) {
    footerIcon = null;
  }

  const buttonActions = {
    continue: () => handleNext(),
    close: () => closeForm(),
  };

  const buttonComponent = button?.display &&
    button?.action &&
    button?.label && (
      <CustomButton
        variant="yellow"
        onClick={buttonActions[button.action] || (() => {})}
      >
        {button.label}
      </CustomButton>
    );

  // Determinar si debemos mostrar una pantalla especial o el formulario
  const isScreenPage =
    formStateLoading ||
    formConfigLoading ||
    form_found === false ||
    form_already_submitted === true ||
    ya_fue_completado === true ||
    completed === true ||
    formSubmitted === true ||
    currentIndex === -1 ||
    currentIndex >= totalSteps ||
    config !== null;

  // Effects
  useEffect(() => {
    dispatch(fetchFormConfig(type_of_form));
  }, [dispatch, type_of_form]);

  useEffect(() => {
    if (web_uri) {
      // Validación adicional del web_uri
      if (!web_uri.trim()) {
        return;
      }

      if (web_uri.length < 3) {
        return;
      }

      dispatch(fetchFormState({ web_uri }));
      dispatch(fetchPages({ web_uri }));
    }
  }, [dispatch, web_uri]);

  // Efecto para inicializar el formulario cuando se cargan los datos
  useEffect(() => {
 
    if (
      form_found === true &&
      form_already_submitted === false &&
      ya_fue_completado === false &&
      completed === false &&
      totalSteps > 0 &&
      currentIndex === -1
    ) {
      setCurrentIndex(0);
    }
  }, [
    form_found,
    form_already_submitted,
    ya_fue_completado,
    completed,
    totalSteps,
    currentIndex,
  ]);

  useEffect(() => {
    const pageId = sortedPages[currentIndex]?.id;

    if (pageId && currentIndex >= 0 && currentIndex < totalSteps) {
      setIsLoadingQuestions(true);
      dispatch(fetchQuestions(pageId))
        .then(() => {
          setIsLoadingQuestions(false);
        })
        .catch(() => {
          setIsLoadingQuestions(false);
        });
    } else {
      setIsLoadingQuestions(false);
    }
  }, [dispatch, sortedPages, currentIndex, totalSteps]);

  // Handlers
  const handleAnswerChange = (questionId, value) => {
    setAnswerValue(questionId, value);
  };

  const closeForm = () => (window.location.href = "https://www.belozfi.com.mx");

  const handleNext = async () => {
    if (currentIndex === -1) {
      setCurrentIndex(0);
      return;
    }

    if (currentIndex < totalSteps - 1) {
      const currentQuestions = questions.filter((q) =>
        sortedPages[currentIndex]?.questions?.includes(q.id)
      );

      if (currentQuestions.length > 0) {
        const validation = validateAnswers(answers, currentQuestions);

        if (!validation.isValid) {
          alert(
            `Por favor, responde todas las preguntas de esta página antes de continuar.\n\nErrores: ${validation.errors.join(
              ", "
            )}`
          );
          return;
        }
      }
    }

    if (currentIndex === totalSteps - 1) {
      // Última página - enviar respuestas


      const validation = validateAnswers(questions);

      if (!validation.isValid) {
        alert(`Error de validación: ${validation.errors.join(", ")}`);
        return;
      }

      // Validación adicional: verificar que no estemos enviando un formulario vacío
      const answerKeys = Object.keys(answers);
      if (answerKeys.length === 0) {
        alert(
          "Error: No se pueden enviar respuestas vacías. Por favor, responde al menos una pregunta."
        );
        return;
      }

      BehaviorTrackerService.stopTracking();

      try {
        await dispatch(
          submitAnswers({
            web_uri,
            answers,
            behavioral_answers: BehaviorTrackerService.getTrackingData(),
            form_id,
            questions, 
          })
        ).unwrap();

        setFormSubmitted(true);
      } catch (error) {
        if (error.errors && error.errors.length > 0) {
          alert(`Error al enviar el formulario: ${error.errors.join(", ")}`);
        } else if (error.message) {
          alert(`Error al enviar el formulario: ${error.message}`);
        } else {
          alert("Error al enviar el formulario. Por favor, intenta de nuevo.");
        }
      }
    } else {
      // Avanzar a la siguiente página
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else if (currentIndex === 0) {
      setCurrentIndex(-1);
    }
  };

  if (formStateLoading || formConfigLoading) {
    return (
      <div style={styles.mainContainerStyle}>
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <p>Cargando formulario...</p>
        </div>
      </div>
    );
  }

  if (formStateError && !formStateLoading) {
    return (
      <div style={styles.mainContainerStyle}>
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            maxWidth: "600px",
            margin: "0 auto",
          }}
        >
          <h2 style={{ color: "#ff0000", marginBottom: "1rem" }}>
            ❌ Error al cargar el formulario
          </h2>
          <p style={{ marginBottom: "1rem" }}>
            Ha ocurrido un error al cargar el formulario. Por favor, intenta de
            nuevo.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: "#007bff",
              color: "white",
              border: "none",
              padding: "10px 20px",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Recargar página
          </button>
        </div>
      </div>
    );
  }

  // Mostrar pantalla especial si es necesario
  if (isScreenPage) {
    return (
      <div style={styles.mainContainerStyle}>
        <FormScreenTemplate
          title={hero || title}
          paragraph={bodyText || description}
          showButton={Boolean(button?.display)}
          button={buttonComponent}
          footerIcon={footerIcon}
          fitoComponentProps={{
            display: Boolean(fito?.display),
            message: fito?.message || "",
            emotion: fito?.emotion || "happy",
          }}
        />
      </div>
    );
  }

  return (
    <div style={styles.mainContainerStyle}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <StepFormTemplate
        title={title}
        description={description}
        currentStep={currentIndex + 1}
        totalSteps={totalSteps}
        submitEnabled={submitEnabled}
        onSubmit={handleNext}
        onPrevious={handlePrevious}
        onClose={closeForm}
        loading={submitStatus === "loading"}
        error={submitError}
        validationErrors={validationErrors}
      >
        {isLoadingQuestions || questions.length === 0 ? (
          <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
            <div style={{ marginBottom: "1rem" }}>
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  border: "4px solid #f3f3f3",
                  borderTop: "4px solid #FACE07",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto",
                }}
              ></div>
            </div>
            <p style={{ fontWeight: "bold", marginBottom: "0.5rem" }}>
              {isLoadingQuestions
                ? "Cargando preguntas..."
                : "No hay preguntas disponibles"}
            </p>
            <p style={{ fontSize: "0.9rem", opacity: 0.7 }}>
              Página {currentIndex + 1} de {totalSteps}
            </p>
          </div>
        ) : (
          <FormPage
            questions={questions}
            answers={answers}
            onAnswerChange={handleAnswerChange}
            setSubmitEnabled={setSubmitEnabled}
          />
        )}
      </StepFormTemplate>
    </div>
  );
};

GenericForm.propTypes = {
  type_of_form: PropTypes.string,
};

export default GenericForm;
